import React, { useEffect, useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartArea,
  ChartData
} from 'chart.js';
import { BenchmarkData, loadBenchmarkData, getBenchmarkDataForRefrigerant, getErrorCategoryColor } from '../lib/benchmark-data';
import { TemperatureUnit, PressureUnit } from '../types';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface ErrorChartProps {
  selectedRefrigerant: string;
  temperatureUnit: TemperatureUnit;
  pressureUnit: PressureUnit;
}

// Gradient function for line coloring
function getGradient(
  ctx: CanvasRenderingContext2D,
  chartArea: ChartArea | undefined,
  categories: string[],
  getColor: (cat: string) => string
): CanvasGradient {
  if (categories.length === 0) {
    const grad = ctx.createLinearGradient(0, 0, 100, 0);
    grad.addColorStop(0, '#2b2e75');
    grad.addColorStop(1, '#2b2e75');
    return grad;
  }
  const left = Number(chartArea?.left ?? 0);
  const right = Number(chartArea?.right ?? 100);
  const grad = ctx.createLinearGradient(left as number, 0, right as number, 0);
  if (categories.length === 1) {
    grad.addColorStop(0.5, getColor(categories[0]));
  } else {
    for (let i = 0; i < categories.length; i++) {
      const rel = i / (categories.length - 1);
      grad.addColorStop(rel, getColor(categories[i]));
    }
  }
  return grad;
}

export const ErrorChart: React.FC<ErrorChartProps> = ({ selectedRefrigerant, temperatureUnit, pressureUnit }) => {
  const [benchmarkData, setBenchmarkData] = useState<BenchmarkData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load benchmark data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await loadBenchmarkData();
        setBenchmarkData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load benchmark data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Get data for the selected refrigerant
  const refrigerantData = useMemo(() => {
    return getBenchmarkDataForRefrigerant(benchmarkData, selectedRefrigerant);
  }, [benchmarkData, selectedRefrigerant]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!refrigerantData) return null;

    // Separate data by test type
    const bubblePointData = refrigerantData.errors.filter(e => e.testType === 'bubble_point');
    const dewPointData = refrigerantData.errors.filter(e => e.testType === 'dew_point');

    // Create datasets
    const datasets = [];

    // Bubble point line (if data exists)
    if (bubblePointData.length > 0) {
      const sortedBubbleData = bubblePointData.sort((a, b) => a.temperature - b.temperature);
      const bubbleCategories = sortedBubbleData.map(e => e.category);
      datasets.push({
        label: 'Bubble Point',
        data: sortedBubbleData.map(error => ({
          x: error.temperature,
          y: error.absoluteError
        })),
        borderColor: function(context: any) {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!ctx || !chartArea) return '#1976d2'; // blue fallback
          return getGradient(ctx, chartArea, bubbleCategories, getErrorCategoryColor);
        },
        pointBackgroundColor: Array(sortedBubbleData.length).fill('#1976d2'),
        pointBorderColor: Array(sortedBubbleData.length).fill('#1976d2'),
        pointStyle: 'circle',
        borderWidth: 3,
        fill: false,
        tension: 0.1,
        pointRadius: 6,
        showLine: true,
        type: 'line' as const,
        hidden: false,
        datalabels: { display: false },
      });
    }

    // Dew point line (if data exists)
    if (dewPointData.length > 0) {
      const sortedDewData = dewPointData.sort((a, b) => a.temperature - b.temperature);
      const dewCategories = sortedDewData.map(e => e.category);
      datasets.push({
        label: 'Dew Point',
        data: sortedDewData.map(error => ({
          x: error.temperature,
          y: error.absoluteError
        })),
        borderColor: function(context: any) {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!ctx || !chartArea) return '#ff9800'; // orange fallback
          return getGradient(ctx, chartArea, dewCategories, getErrorCategoryColor);
        },
        pointBackgroundColor: Array(sortedDewData.length).fill('#ff9800'),
        pointBorderColor: Array(sortedDewData.length).fill('#ff9800'),
        pointStyle: 'circle',
        borderWidth: 3,
        fill: false,
        tension: 0.1,
        pointRadius: 6,
        pointHoverRadius: 6,
        showLine: true,
        type: 'line' as const,
        hidden: false,
        datalabels: { display: false },
      });
    }

    // If neither bubble nor dew point data exists, plot a single curve for all points
    if (bubblePointData.length === 0 && dewPointData.length === 0 && refrigerantData.errors.length > 0) {
      const sortedData = [...refrigerantData.errors].sort((a, b) => a.temperature - b.temperature);
      const categories = sortedData.map(e => e.category);
      datasets.push({
        label: 'Saturation Curve',
        data: sortedData.map(error => ({
          x: error.temperature,
          y: error.absoluteError
        })),
        borderColor: function(context: any) {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!ctx || !chartArea) return '#2b2e75'; // fallback
          return getGradient(ctx, chartArea, categories, getErrorCategoryColor);
        },
        pointBackgroundColor: categories.map(cat => getErrorCategoryColor(cat)),
        pointBorderColor: categories.map(cat => getErrorCategoryColor(cat)),
        pointStyle: 'circle',
        borderWidth: 3,
        fill: false,
        tension: 0.1,
        pointRadius: 6,
        showLine: true,
        type: 'line' as const,
        hidden: false,
        datalabels: { display: false },
      });
    }

    return {
      datasets
    } as ChartData<'line'>;
  }, [refrigerantData]);

  // Chart configuration
  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
        text: `Calculation Accuracy Analysis for ${selectedRefrigerant}`,
      },
      tooltip: {
        callbacks: {
          title: (context: any) => {
            const point = context[0];
            const tempUnit = temperatureUnit === TemperatureUnit.C ? '°C' : 
                           temperatureUnit === TemperatureUnit.F ? '°F' : 'K';
            return `Temperature: ${point.parsed.x.toFixed(2)} ${tempUnit}`;
          },
          label: (context: any) => {
            const point = context[0];
            const data = point.raw;
            const tempUnit = temperatureUnit === TemperatureUnit.C ? '°C' : 
                           temperatureUnit === TemperatureUnit.F ? '°F' : 'K';
            const pressureUnitLabel = pressureUnit === PressureUnit.KPAA ? 'kPa' :
                                    pressureUnit === PressureUnit.BARA ? 'bar' :
                                    pressureUnit === PressureUnit.PSIA ? 'psi' : 'Pa';
            const labels = [
              `Absolute Error: ${data.y.toFixed(4)} ${tempUnit}`,
              `Pressure: ${data.pressure?.toFixed(2) ?? ''} ${pressureUnitLabel}`,
              `Test Type: ${data.testType ?? ''}`
            ];
            if (data.relativeError !== null && data.relativeError !== undefined) {
              labels.push(`Relative Error: ${(data.relativeError * 100).toFixed(2)}%`);
            }
            return labels;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'linear',
        display: true,
        title: {
          display: true,
          text: `Temperature (${temperatureUnit === TemperatureUnit.C ? '°C' : 
                               temperatureUnit === TemperatureUnit.F ? '°F' : 'K'})`,
        },
      },
      y: {
        type: 'linear',
        display: true,
        title: {
          display: true,
          text: `Absolute Error (${temperatureUnit === TemperatureUnit.C ? '°C' : 
                                  temperatureUnit === TemperatureUnit.F ? '°F' : 'K'})`,
        },
        beginAtZero: true,
        max: refrigerantData?.summary.maxError && refrigerantData.summary.maxError < 1 ? 1 : undefined,
      },
    },
  };

  if (loading) {
    return (
      <div className="chart-section">
        <h2>Calculation Accuracy Analysis</h2>
        <div className="chart-container">
          <div className="loading-message">Loading benchmark data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-section">
        <h2>Calculation Accuracy Analysis</h2>
        <div className="chart-container">
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        </div>
      </div>
    );
  }

  if (!refrigerantData) {
    return (
      <div className="chart-section">
        <h2>Calculation Accuracy Analysis</h2>
        <div className="chart-container">
          <div className="no-data-message">
            No benchmark data available for {selectedRefrigerant}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-section">
      <h2>{`Calculation Accuracy Analysis for ${selectedRefrigerant}`}</h2>
      {/* Summary statistics */}
      <div className="accuracy-summary">
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">Total Tests:</span>
            <span className="stat-value">{refrigerantData.summary.totalTests}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Average Error:</span>
            <span className="stat-value">
              {refrigerantData.summary.averageError.toFixed(4)} 
              {temperatureUnit === TemperatureUnit.C ? '°C' : 
               temperatureUnit === TemperatureUnit.F ? '°F' : 'K'}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Max Error:</span>
            <span className="stat-value">
              {refrigerantData.summary.maxError.toFixed(4)} 
              {temperatureUnit === TemperatureUnit.C ? '°C' : 
               temperatureUnit === TemperatureUnit.F ? '°F' : 'K'}
            </span>
          </div>
        </div>
        
        <div className="accuracy-distribution">
          <h4>Accuracy Distribution:</h4>
          <div className="distribution-bars">
            {Object.entries(refrigerantData.summary.accuracyDistribution).map(([category, count]) => (
              <div key={category} className="distribution-item">
                <span className="category-label" style={{ color: getErrorCategoryColor(category) }}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}:
                </span>
                <span className="category-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Error line plot */}
      <div className="chart-container">
        <Line options={chartOptions} data={chartData!} />
      </div>
      
      {/* Legend explanation */}
      <div className="chart-legend">
        <h4>Error Categories:</h4>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: getErrorCategoryColor('excellent') }}></span>
            <span>Excellent (≤0.5 {temperatureUnit === TemperatureUnit.C ? '°C' : 
                                   temperatureUnit === TemperatureUnit.F ? '°F' : 'K'})</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: getErrorCategoryColor('good') }}></span>
            <span>Good (≤1.0 {temperatureUnit === TemperatureUnit.C ? '°C' : 
                              temperatureUnit === TemperatureUnit.F ? '°F' : 'K'})</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: getErrorCategoryColor('acceptable') }}></span>
            <span>Acceptable (≤2.0 {temperatureUnit === TemperatureUnit.C ? '°C' : 
                                        temperatureUnit === TemperatureUnit.F ? '°F' : 'K'})</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: getErrorCategoryColor('poor') }}></span>
            <span>Poor (≤5.0 {temperatureUnit === TemperatureUnit.C ? '°C' : 
                              temperatureUnit === TemperatureUnit.F ? '°F' : 'K'})</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: getErrorCategoryColor('unacceptable') }}></span>
            <span>Unacceptable (&gt;5.0 {temperatureUnit === TemperatureUnit.C ? '°C' : 
                                         temperatureUnit === TemperatureUnit.F ? '°F' : 'K'})</span>
          </div>
        </div>
        <div className="legend-items" style={{ marginTop: '0.5em' }}>
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#1976d2', border: '2px solid #1976d2', borderRadius: '50%', width: 16, height: 16, display: 'inline-block', marginRight: 8 }}></span>
            <span>Bubble Point</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#ff9800', border: '2px solid #ff9800', borderRadius: '50%', width: 16, height: 16, display: 'inline-block', marginRight: 8 }}></span>
            <span>Dew Point</span>
          </div>
        </div>
      </div>
    </div>
  );
};